import { SecureStorage } from "@aparajita/capacitor-secure-storage";

/**
 * spec11 FR4: the offline copy is encrypted with AES-GCM (WebCrypto) under a random 256-bit
 * key that lives only in Keystore-backed secure storage, next to the session token.
 * Imported statically for the same reason as tokens.ts (the plugin is a thenable Proxy).
 */
const KEY = "offline-key";

/** An encrypted record: a fresh 96-bit IV per record, as AES-GCM requires. */
export interface Sealed {
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
}

export interface Cipher {
  encrypt: (text: string) => Promise<Sealed>;
  decrypt: (sealed: Sealed) => Promise<string>;
}

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

export async function createCipher(raw: Uint8Array<ArrayBuffer>): Promise<Cipher> {
  const key = await crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  return {
    async encrypt(text) {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoder.encode(text));
      return { iv, data };
    },
    async decrypt({ iv, data }) {
      return decoder.decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data));
    },
  };
}

/** The device's key; with `create`, a new one is made when there is none yet. */
export async function loadKey(create: boolean): Promise<Uint8Array<ArrayBuffer> | null> {
  const stored = await SecureStorage.getItem(KEY);
  if (typeof stored === "string" && stored !== "") return fromBase64(stored);
  if (!create) return null;
  const raw = crypto.getRandomValues(new Uint8Array(32));
  await SecureStorage.setItem(KEY, toBase64(raw));
  return raw;
}

/** FR15: without the key the old store can never be read again. */
export async function deleteKey(): Promise<void> {
  await SecureStorage.removeItem(KEY);
}
